import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  DetallesEmergencias,
  IArchivosAdjuntos,
  ICreateEmergencia,
  IDestinatarioAsistencia,
  IEmergencia,
  IEmergenciaMedica,
  IEventoGenerico,
  IFilter,
  IGeoJSONPoint,
  IListadoCategoria,
  IPersonalSalud,
  IQueryParam,
  IUbicacion,
  IUpdateEmergencia,
  PrioridadEmergenciaMedica,
  SignosVitales,
} from 'modelos/src';
import { Subscription, firstValueFrom } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { EmergenciasService } from '../../../../../auxiliares/servicios/http/emergencias.service';
import {
  ArchivoSubido,
  SubirArchivoComponent,
} from '../../../../../auxiliares/subir-archivos-extendido/subir-archivos.component';
import { MapaUbicarComponent } from '../../../../../auxiliares/mapas/mapa-ubicar/mapa-ubicar.component';
import { ListadoCategoriasSelectorComponent } from '../../../../../componentes/selectores/listado-categoria-selector/listado-categoria-selector.component';
import { LoginService } from '../../../../login/login.service';
import {
  CrearEditarDestinatarioAsistenciaComponent,
  ICrearEditarPacienteData,
} from '../../entidades-emergencias/destinatario-asistencia/crear-editar-destinatario-asistencia/crear-editar-destinatario-asistencia.component';
import {
  IPresentacion,
  PRIORIDADES,
  SituacionEmergencia,
  presentacionPrioridad,
  presentacionSituacion,
  situacionDe,
} from '../../estado-emergencia';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';

/** Situaciones que se pueden elegir al crear (la importada no). */
type SituacionElegible = Exclude<SituacionEmergencia, 'Importada'>;

interface OpcionSituacion {
  valor: SituacionElegible;
  presentacion: IPresentacion;
  descripcion: string;
}

/** Los ítems de síntomas y diagnósticos que terminan en "…" se completan a mano. */
const SUSPENSIVOS = /(\.{3}|…)$/;

@Component({
  selector: 'app-crear-editar-emergencia',
  templateUrl: './crear-editar-emergencia.component.html',
  styleUrls: [
    '../../form-dialogo.scss',
    './crear-editar-emergencia.component.scss',
  ],
  standalone: true,
  imports: [
    AuxiliaresModule,
    SubirArchivoComponent,
    MapaUbicarComponent,
    ListadoCategoriasSelectorComponent,
  ],
})
export class CrearEditarEmergenciaComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  private service = inject(EmergenciasService);
  private formularios = inject(FormularioDialogService);
  helper = inject(HelperService);
  // Siempre en diálogo: { edit } o { id } edita; sin datos, crea.
  data = inject<{ edit?: IEmergencia; id?: string } | null>(MAT_DIALOG_DATA, {
    optional: true,
  });
  dialogRef =
    inject<MatDialogRef<CrearEditarEmergenciaComponent>>(MatDialogRef);

  public id?: string | null;
  public emergencia?: IEmergencia;
  public loading = true;
  public guardando = false;
  public title = '';
  public subtitle = '';

  public clienteId?: string = LoginService.getCliente()?._id;
  private usuarioPropio = LoginService.getUsuario();

  // Forms
  public form?: FormGroup;
  public formUbicacion?: FormGroup;
  get ubicacion(): FormGroup {
    return this.formUbicacion?.get('ubicacion') as FormGroup;
  }
  public centrarA?: IGeoJSONPoint;

  // Situación
  public readonly opcionesSituacion: OpcionSituacion[] = [
    {
      valor: 'Auxilio',
      presentacion: presentacionSituacion('Auxilio'),
      descripcion:
        'Se despacha un móvil. La ubicación es obligatoria y la emergencia arranca en Pendiente.',
    },
    {
      valor: 'Seguimiento',
      presentacion: presentacionSituacion('Seguimiento'),
      descripcion:
        'Se sigue al paciente sin despachar un móvil. La ubicación es opcional y arranca en Pendiente.',
    },
    {
      valor: 'Registrada finalizada',
      presentacion: presentacionSituacion('Registrada finalizada'),
      descripcion:
        'El paciente ya fue atendido. Se registra cerrada, con los datos de la atención.',
    },
    {
      valor: 'Llamada',
      presentacion: presentacionSituacion('Llamada'),
      descripcion:
        'Sólo queda registrada la llamada. Se guarda como finalizada, sin ubicación ni móvil.',
    },
  ];
  public situacion: SituacionEmergencia = 'Auxilio';

  // Prioridad (código)
  public readonly prioridades = PRIORIDADES.map((p) => ({
    valor: p,
    presentacion: presentacionPrioridad(p),
  }));

  // Listas
  public pacientes: IDestinatarioAsistencia[] = [];
  private pacientes$?: Subscription;
  public solicitantes: string[] = [];
  private solicitantePredeterminado = '';
  public medicos: IPersonalSalud[] = [];
  public enfermeros: IPersonalSalud[] = [];
  public hospitales: IUbicacion[] = [];
  private listadoSintomas: IListadoCategoria[] = [];

  // Síntomas y diagnóstico ("…" se completa a mano)
  public sintomasIniciales: string[] = [];
  public sintomasComplementos = new Map<string, string>();
  public diagnosticoInicial: string[] = [];
  public diagnosticoSeleccionado = '';
  private diagnosticoTocado = false;

  // Adjuntos
  public archivosSubidos: ArchivoSubido[] = [];
  public subiendoArchivos = false;

  private ultimoTelefonoAutomatico?: string;

  get esEdicion(): boolean {
    return !!this.id;
  }

  get esAuxilio(): boolean {
    return this.situacion === 'Auxilio';
  }

  get llevaUbicacion(): boolean {
    return this.situacion === 'Auxilio' || this.situacion === 'Seguimiento';
  }

  get esRegistradaFinalizada(): boolean {
    return this.situacion === 'Registrada finalizada';
  }

  get puedeGuardar(): boolean {
    const permiso = this.esEdicion
      ? this.helper.tienePermiso('Emergencias Médicas - Editar')
      : this.helper.tienePermiso('Emergencias Médicas - Crear');
    return (
      permiso &&
      !!this.form?.valid &&
      (!this.llevaUbicacion || !!this.formUbicacion?.valid) &&
      !this.guardando &&
      !this.subiendoArchivos
    );
  }

  get situacionActual(): IPresentacion {
    return presentacionSituacion(this.situacion);
  }

  // Formularios

  private crearFormularios(): void {
    const e = this.emergencia;
    const em = e?.emergenciaMedica;
    const det = e?.ultimoEventoEmergencia?.detallesEmergencias;
    this.form = new FormGroup({
      prioridadApertura: new FormControl<PrioridadEmergenciaMedica | null>(
        (e?.prioridadApertura as PrioridadEmergenciaMedica) ?? null,
        Validators.required,
      ),
      solicitante: new FormControl<string | null>(
        e?.solicitante || this.solicitantePredeterminado || null,
        Validators.required,
      ),
      telefono: new FormControl<string | null>(e?.telefono ?? null),
      idDestinatarioAsistencia: new FormControl<string | null>(
        e?.idDestinatarioAsistencia ?? null,
        Validators.required,
      ),
      sintomas: new FormControl<string[]>(em?.sintomasApertura ?? []),
      diagnosticoComplemento: new FormControl<string>(''),
      observaciones: new FormControl<string | null>(e?.observaciones ?? null),
      // Registrada finalizada
      idsMedicos: new FormControl<string[]>(det?.idsMedicos ?? []),
      idsEnfermeros: new FormControl<string[]>(det?.idsEnfermeros ?? []),
      idHospital: new FormControl<string | null>(det?.idHospital ?? null),
      tratamiento: new FormControl<string | null>(em?.tratamiento ?? null),
      medicacion: new FormControl<string | null>(em?.medicacion ?? null),
      tensionArterial: new FormControl<number | null>(
        em?.signosVitales?.tensionArterial ?? null,
      ),
      frecuenciaRespiratoria: new FormControl<number | null>(
        em?.signosVitales?.frecuenciaRespiratoria ?? null,
      ),
      frecuenciaCardiaca: new FormControl<number | null>(
        em?.signosVitales?.frecuenciaCardiaca ?? null,
      ),
      saturacionOxigeno: new FormControl<number | null>(
        em?.signosVitales?.saturacionOxigeno ?? null,
      ),
      temperatura: new FormControl<number | null>(
        em?.signosVitales?.temperatura ?? null,
      ),
      traslado: new FormControl<boolean>(em?.traslado?.traslado ?? false),
      trasladoDireccion: new FormControl<string | null>(
        em?.traslado?.direccion ?? null,
      ),
      aceptaTraslado: new FormControl<boolean>(
        em?.traslado?.aceptaTraslado ?? true,
      ),
    });

    this.formUbicacion = new FormGroup({
      direccion: new FormControl<string | null>(e?.direccion ?? null),
      ubicacion: new FormGroup({
        type: new FormControl('Point'),
        coordinates: new FormControl(e?.ubicacionDestino?.coordinates ?? null),
      }),
    });
    this.actualizarValidacionUbicacion();

    // El teléfono se completa con el del paciente mientras no se lo edite
    this.form
      .get('idDestinatarioAsistencia')
      ?.valueChanges.subscribe((idPaciente: string) => {
        const paciente = this.pacientes.find((p) => p._id === idPaciente);
        if (!paciente) return;
        const nuevo = paciente.telefono || paciente.telefonoAlternativo || '';
        const actual = this.form?.get('telefono')?.value;
        if (!actual || actual === this.ultimoTelefonoAutomatico) {
          this.form?.patchValue({ telefono: nuevo });
        }
        this.ultimoTelefonoAutomatico = nuevo;
      });
  }

  public elegirSituacion(valor: SituacionElegible): void {
    if (this.esEdicion) return;
    this.situacion = valor;
    this.actualizarValidacionUbicacion();
  }

  private actualizarValidacionUbicacion(): void {
    const direccion = this.formUbicacion?.get('direccion');
    const coords = this.formUbicacion?.get('ubicacion.coordinates');
    if (this.esAuxilio) {
      direccion?.setValidators(Validators.required);
      coords?.setValidators(Validators.required);
    } else {
      direccion?.clearValidators();
      coords?.clearValidators();
    }
    direccion?.updateValueAndValidity();
    coords?.updateValueAndValidity();
  }

  public patchDireccion(direccion: string): void {
    this.formUbicacion?.patchValue({ direccion });
  }

  // Listas

  private listarPacientes(): void {
    // $ne y no igualdad: los pacientes viejos pueden no tener tipoEmergencia
    const filter = {
      tipoEmergencia: { $ne: 'Bombero' },
    } as IFilter<IDestinatarioAsistencia>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'nombre',
      select:
        'nombre apellido sexo dni edad obraSocial telefono telefonoAlternativo email',
      unlimited: true,
      includeChildren: true,
    };
    this.pacientes$?.unsubscribe();
    this.pacientes$ = this.lista2
      .destinatarioAsistencias(query, 30)
      .subscribe((data) => (this.pacientes = data?.datos || []));
  }

  private async listarCliente(): Promise<void> {
    if (!this.clienteId) return;
    try {
      const cliente = await firstValueFrom(this.lista2.cliente(this.clienteId));
      this.solicitantePredeterminado =
        cliente?.config?.solicitantePredeterminado || '';
      this.solicitantes = this.ordenarSolicitantes(
        cliente?.config?.solicitantesEmergencias || [],
      );
    } catch (error) {
      console.error(error);
    }
  }

  /** El predeterminado primero, después "Particular" y el resto. */
  private ordenarSolicitantes(lista: string[]): string[] {
    const ordenados: string[] = [];
    if (this.solicitantePredeterminado) {
      ordenados.push(this.solicitantePredeterminado);
    }
    if (lista.includes('Particular') && !ordenados.includes('Particular')) {
      ordenados.push('Particular');
    }
    lista.forEach((s) => {
      if (!ordenados.includes(s)) ordenados.push(s);
    });
    return ordenados;
  }

  private async listarPersonal(): Promise<void> {
    const query = (rol: string): IQueryParam => ({
      filter: JSON.stringify({ rol } as IFilter<IPersonalSalud>),
      sort: 'nombre',
      select: 'nombre',
      unlimited: true,
      includeChildren: true,
    });
    try {
      const [medicos, enfermeros] = await Promise.all([
        firstValueFrom(this.lista2.personalSaluds(query('Médico'))),
        firstValueFrom(this.lista2.personalSaluds(query('Enfermero'))),
      ]);
      this.medicos = medicos?.datos || [];
      this.enfermeros = enfermeros?.datos || [];
    } catch (error) {
      console.error(error);
    }
  }

  private async listarHospitales(): Promise<void> {
    const query: IQueryParam = {
      sort: 'identificacion',
      select: 'identificacion valores.gestion',
      unlimited: true,
      includeChildren: true,
    };
    try {
      const data = await firstValueFrom(this.lista2.hospitals(query));
      this.hospitales = data?.datos || [];
    } catch (error) {
      console.error(error);
    }
  }

  private async listarSintomas(): Promise<void> {
    const query: IQueryParam = {
      filter: JSON.stringify({ categoria: 'Sintomas' }),
      sort: 'nombre',
      unlimited: true,
    };
    try {
      const data = await firstValueFrom(this.lista2.listadoCategorias(query));
      this.listadoSintomas = data?.datos || [];
    } catch (error) {
      console.error(error);
    }
  }

  // Paciente

  public crearPaciente(): void {
    // Sin id: crea. Al cerrar devuelve el paciente guardado
    const data: ICrearEditarPacienteData = { desdeEmergencia: true };
    const ref = this.formularios.abrir(
      CrearEditarDestinatarioAsistenciaComponent,
      { tamano: 'mediano', data },
    );
    ref.afterClosed().subscribe((creado?: IDestinatarioAsistencia) => {
      if (!creado?._id) return;
      this.pacientes = [...this.pacientes, creado];
      this.form?.patchValue({ idDestinatarioAsistencia: creado._id });
      this.listarPacientes();
    });
  }

  public buscarPaciente = (
    term: string,
    item: IDestinatarioAsistencia,
  ): boolean => {
    const t = term.toLowerCase();
    return [
      item.nombre,
      item.apellido,
      item.dni,
      item.telefono,
      item.obraSocial,
      item.email,
    ].some((v) => !!v && v.toLowerCase().includes(t));
  };

  // Síntomas

  public onSintomasChange(categorias: IListadoCategoria[]): void {
    const nombres = categorias.map((c) => c.nombre).filter(Boolean) as string[];
    this.form?.patchValue({ sintomas: nombres });
    // Se olvidan los complementos de los que ya no están
    const actuales = new Set(nombres);
    [...this.sintomasComplementos.keys()].forEach((s) => {
      if (!actuales.has(s)) this.sintomasComplementos.delete(s);
    });
  }

  public requiereComplemento(nombre: string): boolean {
    return SUSPENSIVOS.test(nombre || '');
  }

  public sinSuspensivos(nombre: string): string {
    return (nombre || '').replace(SUSPENSIVOS, '');
  }

  get sintomasConComplemento(): string[] {
    return (this.form?.value?.sintomas || []).filter((s: string) =>
      this.requiereComplemento(s),
    );
  }

  public setComplemento(sintoma: string, valor: string): void {
    this.sintomasComplementos.set(sintoma, valor);
  }

  private sintomasCompletos(): string[] {
    return (this.form?.value?.sintomas || []).map((s: string) => {
      if (!this.requiereComplemento(s)) return s;
      const complemento = this.sintomasComplementos.get(s) || '';
      return complemento ? this.sinSuspensivos(s) + complemento : s;
    });
  }

  /** Al editar: "Dolor de cabeza intenso" → "Dolor de cabeza…" + "intenso". */
  private restaurarComplementos(): void {
    const guardados = this.emergencia?.emergenciaMedica?.sintomasApertura || [];
    const bases = this.listadoSintomas
      .map((s) => s.nombre || '')
      .filter((n) => this.requiereComplemento(n));
    const sintomas = guardados.map((guardado) => {
      const base = bases.find((b) => {
        const raiz = this.sinSuspensivos(b);
        return guardado !== b && guardado.startsWith(raiz);
      });
      if (!base) return guardado;
      this.sintomasComplementos.set(
        base,
        guardado.slice(this.sinSuspensivos(base).length),
      );
      return base;
    });
    this.form?.patchValue({ sintomas });
  }

  // Diagnóstico

  public onDiagnosticoChange(categoria: IListadoCategoria | null): void {
    this.diagnosticoTocado = true;
    this.diagnosticoSeleccionado = categoria?.nombre || '';
    this.form?.patchValue({ diagnosticoComplemento: '' });
  }

  private diagnosticoFinal(): string | undefined {
    if (!this.diagnosticoTocado) {
      return this.emergencia?.emergenciaMedica?.diagnostico || undefined;
    }
    if (!this.diagnosticoSeleccionado) return '';
    if (this.requiereComplemento(this.diagnosticoSeleccionado)) {
      const complemento = this.form?.value?.diagnosticoComplemento || '';
      return complemento
        ? this.sinSuspensivos(this.diagnosticoSeleccionado) + complemento
        : this.diagnosticoSeleccionado;
    }
    return this.diagnosticoSeleccionado;
  }

  // Guardar

  private adjuntos(): IArchivosAdjuntos[] {
    return this.archivosSubidos.map((a) => ({
      fechaSubida: a.fecha.toISOString(),
      descripcion: a.descripcion,
      archivo: a.url,
      nombreOriginal: a.nombreOriginal,
      usuario: this.usuarioPropio?._id,
    }));
  }

  private datosClinicos(): Partial<IEmergenciaMedica> {
    const v = this.form?.value;
    const signos: SignosVitales = {
      tensionArterial: v?.tensionArterial ?? undefined,
      frecuenciaRespiratoria: v?.frecuenciaRespiratoria ?? undefined,
      frecuenciaCardiaca: v?.frecuenciaCardiaca ?? undefined,
      saturacionOxigeno: v?.saturacionOxigeno ?? undefined,
      temperatura: v?.temperatura ?? undefined,
    };
    const haySignos = Object.values(signos).some((x) => x != null);
    return {
      tratamiento: v?.tratamiento || undefined,
      medicacion: v?.medicacion || undefined,
      signosVitales: haySignos ? signos : undefined,
      traslado: {
        traslado: !!v?.traslado,
        direccion: v?.traslado ? v?.trasladoDireccion || undefined : undefined,
        aceptaTraslado: v?.traslado ? !!v?.aceptaTraslado : undefined,
      },
    };
  }

  private datosComunes(): IUpdateEmergencia {
    const v = this.form?.value;
    const sintomasApertura = this.sintomasCompletos();
    const diagnostico = this.diagnosticoFinal();
    const emergenciaMedica: IEmergenciaMedica = {
      ...(this.emergencia?.emergenciaMedica || {}),
      sintomasApertura,
      ...(diagnostico !== undefined ? { diagnostico } : {}),
      ...(this.esRegistradaFinalizada
        ? { ...this.datosClinicos(), sintomasCierre: sintomasApertura }
        : {}),
    };
    const dato: IUpdateEmergencia = {
      prioridadApertura: v?.prioridadApertura,
      solicitante: v?.solicitante,
      telefono: v?.telefono || undefined,
      idDestinatarioAsistencia: v?.idDestinatarioAsistencia,
      observaciones: v?.observaciones || undefined,
      archivosAdjuntos: this.adjuntos(),
      emergenciaMedica,
    };
    const coords = this.formUbicacion?.value?.ubicacion?.coordinates;
    if (this.llevaUbicacion && coords) {
      dato.direccion = this.formUbicacion?.value?.direccion || undefined;
      dato.ubicacionDestino = { type: 'Point', coordinates: coords };
    }
    return dato;
  }

  private async crear(): Promise<IEmergencia> {
    const dato: ICreateEmergencia = {
      ...this.datosComunes(),
      tipo: 'Médica',
      esAuxilio: this.situacion === 'Auxilio',
      enSeguimiento: this.situacion === 'Seguimiento',
      iniciaFinalizada: this.esRegistradaFinalizada,
      asignada: false,
    };
    let infoUltimoEvento: IEventoGenerico | undefined;
    if (this.esRegistradaFinalizada || this.situacion === 'Llamada') {
      // Nacen cerradas: el código de cierre es el de apertura
      dato.prioridadCierre = dato.prioridadApertura;
    }
    if (this.esRegistradaFinalizada) {
      dato.fechaFinalizacion = new Date().toISOString();
      const v = this.form?.value;
      const detallesEmergencias: DetallesEmergencias = {
        idDestinatarioAsistencia: v?.idDestinatarioAsistencia,
        idUsuarioResponsable: this.usuarioPropio?._id,
        idsMedicos: v?.idsMedicos?.length ? v.idsMedicos : undefined,
        idsEnfermeros: v?.idsEnfermeros?.length ? v.idsEnfermeros : undefined,
        idHospital: v?.idHospital || undefined,
      };
      infoUltimoEvento = { detallesEmergencias };
    }
    return this.service.create(dato, infoUltimoEvento);
  }

  public async onSubmit(): Promise<void> {
    if (!this.puedeGuardar) return;
    this.guardando = true;
    try {
      let resultado: IEmergencia;
      if (this.id) {
        resultado = await this.service.update(this.id, this.datosComunes());
        this.helper.notifSuccess('Emergencia actualizada correctamente');
      } else {
        resultado = await this.crear();
        this.helper.notifSuccess('Emergencia creada correctamente');
      }
      this.cerrar(resultado);
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.guardando = false;
    }
  }

  /** Cancelar: cierra el diálogo sin cambios. */
  public cancelar(): void {
    this.dialogRef.close();
  }

  /** Después de guardar: el diálogo devuelve la emergencia guardada. */
  private cerrar(emergencia?: IEmergencia): void {
    this.dialogRef.close(emergencia);
  }

  // Hooks

  async ngOnInit(): Promise<void> {
    this.id = this.data?.edit?._id || this.data?.id;

    await Promise.all([
      this.listarCliente(),
      this.listarPersonal(),
      this.listarHospitales(),
      this.listarSintomas(),
    ]);
    this.listarPacientes();

    if (this.id) {
      try {
        this.emergencia = await this.service.getById(this.id);
      } catch (error) {
        this.helper.notifError(error);
      }
      this.situacion = situacionDe(this.emergencia);
      this.centrarA = this.emergencia?.ubicacionDestino;
      this.archivosSubidos = (this.emergencia?.archivosAdjuntos || []).map(
        (a) => ({
          url: a.archivo || '',
          descripcion: a.descripcion || '',
          fecha: a.fechaSubida ? new Date(a.fechaSubida) : new Date(),
          nombreOriginal: a.nombreOriginal || 'archivo',
          size: 0,
        }),
      );
      this.sintomasIniciales =
        this.emergencia?.emergenciaMedica?.sintomasApertura || [];
      const diagnostico = this.emergencia?.emergenciaMedica?.diagnostico;
      this.diagnosticoInicial = diagnostico ? [diagnostico] : [];
      this.ultimoTelefonoAutomatico = this.emergencia?.telefono;
      this.title = `Editar emergencia #${this.emergencia?.codigo ?? ''}`;
      this.subtitle = presentacionSituacion(this.situacion).label;
    } else {
      this.title = 'Nueva emergencia médica';
      this.subtitle = 'Registrá la llamada y elegí qué hacer con ella';
    }

    this.crearFormularios();
    if (this.id) this.restaurarComplementos();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.pacientes$?.unsubscribe();
  }
}
